import {
  Bell,
  Check,
  ChevronLeft,
  ChevronRight,
  Database,
  Globe2,
  HardDrive,
  Info,
  KeyRound,
  Laptop,
  Link2,
  LogOut,
  Moon,
  Palette,
  Server,
  Settings,
  Shield,
  Sun,
  UserRound,
  Video,
  Wifi,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { AccountSecurityCenter } from '../components/AccountSecurityCenter';
import { apiRequest } from '../api/client';
import { appConfig } from '../config';
import { useAuth } from '../context/AuthContext';
import { useTheme, type ThemePreference } from '../context/ThemeContext';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type SettingsView = 'home' | 'general' | 'notifications' | 'connection' | 'storage' | 'security' | 'about';

interface StorageSettings {
  activeMode: number;
  restartRequired: boolean;
}

interface SettingsItem {
  label: string;
  description: string;
  icon: LucideIcon;
  tone: 'blue' | 'purple' | 'green' | 'orange' | 'teal' | 'gray';
  view?: Exclude<SettingsView, 'home'>;
  to?: string;
}

const groups: { title: string; items: SettingsItem[] }[] = [
  {
    title: 'Приложение',
    items: [
      { label: 'Общие', description: 'Язык, тема, часовой пояс', icon: Settings, tone: 'blue', view: 'general' },
      { label: 'Уведомления', description: 'Push-уведомления и события', icon: Bell, tone: 'purple', view: 'notifications' },
      { label: 'Хранилище', description: 'Текущий режим базы данных', icon: Server, tone: 'orange', view: 'storage' },
    ],
  },
  {
    title: 'Безопасность',
    items: [
      { label: 'Безопасность', description: 'Пароль, 2FA, активные сессии', icon: Shield, tone: 'purple', view: 'security' },
      { label: 'Двухфакторная аутентификация', description: 'Защита вашего аккаунта', icon: KeyRound, tone: 'blue', view: 'security' },
      { label: 'Активные сессии', description: 'Управление устройствами', icon: UserRound, tone: 'orange', view: 'security' },
    ],
  },
  {
    title: 'Интеграции',
    items: [{ label: 'Интеграции', description: 'Telegram, API, Webhook', icon: Link2, tone: 'teal', view: 'connection' }],
  },
  {
    title: 'О приложении',
    items: [{ label: 'О приложении', description: 'Версия, лицензия, поддержка', icon: Info, tone: 'gray', view: 'about' }],
  },
];

function SettingsDetail({ title, onBack, children }: { title: string; onBack: () => void; children: ReactNode }) {
  return (
    <div className="settings-detail">
      <button className="settings-detail__back" type="button" onClick={onBack}>
        <ChevronLeft size={20} aria-hidden="true" />
        <span>Настройки</span>
      </button>
      <h1>{title}</h1>
      {children}
    </div>
  );
}

function SettingsCard({ title, description, icon: Icon, children }: { title: string; description: string; icon: LucideIcon; children: ReactNode }) {
  return (
    <section className="settings-card">
      <div className="settings-card__header"><span aria-hidden="true"><Icon size={19} /></span><div><h2>{title}</h2><p>{description}</p></div></div>
      <div className="settings-card__body">{children}</div>
    </section>
  );
}

export function SettingsPage() {
  const { profile, isLoading, logout } = useAuth();
  const { preference, setPreference } = useTheme();
  const navigate = useNavigate();
  const [view, setView] = useState<SettingsView>('home');
  const [storage, setStorage] = useState<StorageSettings | null>(null);
  const [storageError, setStorageError] = useState<string | null>(null);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isStandalone] = useState(() => window.matchMedia('(display-mode: standalone)').matches);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    const handler = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    if (view !== 'storage' || profile?.role !== 'Administrator') return;
    let active = true;
    setStorage(null);
    setStorageError(null);
    apiRequest<StorageSettings>('/api/storage').then(
      (value) => { if (active) setStorage(value); },
      (error: unknown) => { if (active) setStorageError(error instanceof Error ? error.message : 'Не удалось загрузить хранилище.'); },
    );
    return () => { active = false; };
  }, [view, profile?.role]);

  const installApp = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  const openItem = (item: SettingsItem) => {
    if (item.to) {
      navigate(item.to);
      return;
    }
    if (item.view) setView(item.view);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  if (!profile) return null;

  if (view !== 'home') {
    if (view === 'general') {
      return (
        <div className="page-stack settings-page settings-page--new">
          <SettingsDetail title="Общие" onBack={() => setView('home')}>
            <SettingsCard title="Внешний вид" description="Настройка применяется и сохраняется сразу" icon={Palette}>
              <div className="theme-options" role="radiogroup" aria-label="Цветовая тема">
                {([
                  ['system', 'Системная', Laptop],
                  ['light', 'Светлая', Sun],
                  ['dark', 'Тёмная', Moon],
                ] as const).map(([value, label, Icon]) => (
                  <button key={value} type="button" className={preference === value ? 'is-active' : ''} onClick={() => setPreference(value as ThemePreference)} role="radio" aria-checked={preference === value}>
                    <span><Icon size={22} /></span><strong>{label}</strong>{preference === value && <Check size={16} />}
                  </button>
                ))}
              </div>
            </SettingsCard>
            <SettingsCard title="Регион" description="Параметры отображения дат и текста" icon={Globe2}>
              <div className="settings-info-rows"><div><span>Язык</span><strong>Русский</strong></div><div><span>Часовой пояс</span><strong>{timeZone}</strong></div></div>
            </SettingsCard>
          </SettingsDetail>
        </div>
      );
    }

    if (view === 'security') {
      return (
        <div className="page-stack settings-page settings-page--new">
          <SettingsDetail title="Безопасность" onBack={() => setView('home')}>
            <div className="settings-security-grid"><AccountSecurityCenter /></div>
          </SettingsDetail>
        </div>
      );
    }

    if (view === 'connection') {
      return (
        <div className="page-stack settings-page settings-page--new">
          <SettingsDetail title="Интеграции" onBack={() => setView('home')}>
            <SettingsCard title="API Server" description="Фактическая конфигурация backend" icon={Server}>
              <div className="connection-detail"><span className="connection-detail__icon"><Wifi size={19} /></span><div><strong>REST API</strong><code>{appConfig.apiUrl || 'Same origin'}</code></div><span className="connection-state-pill">Настроено</span></div>
              <div className="connection-detail"><span className="connection-detail__icon"><Wifi size={19} /></span><div><strong>SignalR Hub</strong><code>{appConfig.signalRUrl}</code></div><span className="connection-state-pill">Auto reconnect</span></div>
              <div className="security-note"><Shield size={19} /><div><strong>Отдельная Web-сессия</strong><p>Session cookie недоступна JavaScript, а App-токены не принимаются как Web-сессия.</p></div></div>
            </SettingsCard>
          </SettingsDetail>
        </div>
      );
    }

    if (view === 'storage') {
      return (
        <div className="page-stack settings-page settings-page--new">
          <SettingsDetail title="Хранилище" onBack={() => setView('home')}>
            <SettingsCard title="База данных" description="Фактический режим сервера" icon={Database}>
              {profile.role !== 'Administrator' ? (
                <p className="settings-hint">Сведения о хранилище доступны администратору.</p>
              ) : storageError ? (
                <p className="settings-hint" role="alert">{storageError}</p>
              ) : !storage ? (
                <p className="settings-hint" role="status">Загрузка…</p>
              ) : (
                <>
                  <div className="storage-summary"><div><HardDrive size={22} /><span><strong>Режим хранилища</strong><small>{storage.restartRequired ? 'Для применения изменений нужен перезапуск' : 'Текущий режим сервера'}</small></span></div><span className="tag">{storage.activeMode === 1 ? 'PostgreSQL' : 'SQLite'}</span></div>
                  <p className="settings-hint">Изменение режима выполняется через защищённый API администратора.</p>
                </>
              )}
            </SettingsCard>
          </SettingsDetail>
        </div>
      );
    }

    if (view === 'about') {
      return (
        <div className="page-stack settings-page settings-page--new">
          <SettingsDetail title="О приложении" onBack={() => setView('home')}>
            <SettingsCard title="KorsaApp" description="Панель наблюдения и управления" icon={Info}>
              <div className="about-app-row"><span className="about-app-row__icon"><Video size={22} /></span><div><strong>Установка приложения</strong><p>{isStandalone ? 'Приложение запущено в standalone-режиме.' : 'Добавьте KorsaApp на главный экран для быстрого доступа.'}</p></div><button className="button button--secondary" type="button" disabled={!installPrompt || isStandalone} onClick={() => void installApp()}>{isStandalone ? 'Установлено' : 'Установить'}</button></div>
            </SettingsCard>
          </SettingsDetail>
        </div>
      );
    }

    return (
      <div className="page-stack settings-page settings-page--new">
        <SettingsDetail title="Уведомления" onBack={() => setView('home')}>
          <SettingsCard title="Push-уведомления" description="Получайте важные события от KorsaApp" icon={Bell}>
            <div className="settings-empty-state"><Bell size={24} /><strong>Настройки уведомлений</strong><p>Управление push-уведомлениями появится после подключения сервиса уведомлений.</p></div>
          </SettingsCard>
        </SettingsDetail>
      </div>
    );
  }

  return (
    <div className="page-stack settings-page settings-page--new">
      <header className="settings-home-header"><h1>Настройки</h1></header>

      <button className="settings-profile-card" type="button" onClick={() => navigate('/profile')}>
        <span className="settings-profile-card__avatar" aria-hidden="true">{profile.displayName.trim().charAt(0).toUpperCase()}</span>
        <span className="settings-profile-card__copy"><strong>{profile.displayName}</strong><small>{profile.login ? `@${profile.login}` : 'Профиль'} · {profile.role === 'Administrator' ? 'Администратор' : profile.role === 'Operator' ? 'Оператор' : 'Наблюдатель'}</small></span>
        <ChevronRight size={21} aria-hidden="true" />
      </button>

      <div className="settings-groups">
        {groups.map((group) => (
          <section className="settings-group" key={group.title}>
            <h2>{group.title}</h2>
            <div className="settings-group__list">
              {group.items.map((item) => {
                const Icon = item.icon;
                return <button className="settings-menu-item" type="button" key={item.label} onClick={() => openItem(item)}><span className={`settings-menu-item__icon settings-menu-item__icon--${item.tone}`}><Icon size={20} /></span><span className="settings-menu-item__copy"><strong>{item.label}</strong><small>{item.description}</small></span><ChevronRight size={18} aria-hidden="true" /></button>;
              })}
            </div>
          </section>
        ))}
      </div>

      <button className="settings-logout" type="button" onClick={() => void handleLogout()} disabled={isLoading}><LogOut size={19} /><span>Выйти из аккаунта</span><ChevronRight size={18} aria-hidden="true" /></button>
    </div>
  );
}
